import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import TextArea from '@atlaskit/textarea/text-area';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	getSuggestionProductKeyFromContentUrl,
	type Suggestion,
} from './__types__/RemoteObject.ts';

type QuestionOption = { label: MessageDescriptor; value: number };

type FeedbackQuestion = {
	key: string;
	part: 1 | 2;
	title: MessageDescriptor;
	question: MessageDescriptor;
	options: QuestionOption[];
};

const i18n = defineMessages({
	modalTitle: {
		id: 'space-curation.suggestion-feedback-modal.title',
		defaultMessage: 'Rate suggestion quality',
		description: 'Title of the modal for rating the quality of a content suggestion',
	},
	part1Header: {
		id: 'space-curation.suggestion-feedback-modal.part1-header',
		defaultMessage: 'Part 1: Core Content & Placement',
		description: 'Section header for the first part of the feedback form',
	},
	part2Header: {
		id: 'space-curation.suggestion-feedback-modal.part2-header',
		defaultMessage: 'Part 2: Context & Quality',
		description: 'Section header for the second part of the feedback form',
	},
	skipLabel: {
		id: 'space-curation.suggestion-feedback-modal.skip-label',
		defaultMessage: 'Skip / I do not know',
		description: 'Label for the skip checkbox on each feedback question',
	},
	submitButton: {
		id: 'space-curation.suggestion-feedback-modal.submit',
		defaultMessage: 'Submit',
		description: 'Label for the submit button in the feedback modal',
	},
	cancelButton: {
		id: 'space-curation.suggestion-feedback-modal.cancel',
		defaultMessage: 'Cancel',
		description: 'Label for the cancel button in the feedback modal',
	},

	topicalRelevanceTitle: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.title',
		defaultMessage: 'Topical Relevance',
		description: 'Title for the Topical Relevance feedback question',
	},
	topicalRelevanceQuestion: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.question',
		defaultMessage: 'Is the document about the same topic as the space?',
		description: 'Question text for the Topical Relevance feedback question',
	},
	topicalRelevance3: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.3',
		defaultMessage: 'Highly Relevant – Core part of this topic.',
		description: 'Option label for Topical Relevance score 3',
	},
	topicalRelevance2: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.2',
		defaultMessage: 'Related – Shares the same theme but is tangential.',
		description: 'Option label for Topical Relevance score 2',
	},
	topicalRelevance1: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.1',
		defaultMessage: 'Slightly Related – Only a small portion fits.',
		description: 'Option label for Topical Relevance score 1',
	},
	topicalRelevance0: {
		id: 'space-curation.suggestion-feedback-modal.topical-relevance.0',
		defaultMessage: 'Irrelevant – No logical connection.',
		description: 'Option label for Topical Relevance score 0',
	},

	placementTitle: {
		id: 'space-curation.suggestion-feedback-modal.placement.title',
		defaultMessage: 'Placement',
		description: 'Title for the Placement feedback question',
	},
	placementQuestion: {
		id: 'space-curation.suggestion-feedback-modal.placement.question',
		defaultMessage: 'Is this the right folder suggestion for this content?',
		description: 'Question text for the Placement feedback question',
	},
	placement2: {
		id: 'space-curation.suggestion-feedback-modal.placement.2',
		defaultMessage: 'Perfect Fit – Exactly where it belongs.',
		description:
			'The text is shown as a radio button option label in the Placement rating section of the suggestion feedback modal, representing the highest placement score (2) indicating the suggested content fits perfectly in the current location.',
	},
	placement1: {
		id: 'space-curation.suggestion-feedback-modal.placement.1',
		defaultMessage: 'Acceptable – Not the best spot, but makes sense.',
		description:
			'The text is shown as a radio button option label in the Placement rating section of the suggestion feedback modal, representing a mid-level placement score (1) indicating the suggested content placement is acceptable but not ideal.',
	},
	placement0: {
		id: 'space-curation.suggestion-feedback-modal.placement.0',
		defaultMessage: 'Incorrect – Placement is confusing or wrong.',
		description:
			'The text is shown as a radio button option label in the Placement rating section of the suggestion feedback modal, representing the lowest placement score (0) indicating the suggested content is in the wrong or confusing location.',
	},

	usefulnessTitle: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.title',
		defaultMessage: 'Usefulness',
		description: 'Title for the Usefulness feedback question',
	},
	usefulnessQuestion: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.question',
		defaultMessage: 'Would this doc actually help someone working in this space?',
		description: 'Question text for the Usefulness feedback question',
	},
	usefulness4: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.4',
		defaultMessage: 'Essential – Crucial; the space is incomplete without it.',
		description:
			'The text is shown as a radio button option label in the Usefulness rating section of the suggestion feedback modal, representing the highest usefulness score (4) indicating the suggested content is essential and the space would be incomplete without it.',
	},
	usefulness3: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.3',
		defaultMessage: 'Helpful – Adds clear value and saves time.',
		description:
			'The text is shown as a radio button option label in the Usefulness rating section of the suggestion feedback modal, representing a high usefulness score (3) indicating the suggested content adds clear value and saves time.',
	},
	usefulness2: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.2',
		defaultMessage: "Neutral – Belongs there, but doesn't change much.",
		description:
			'The text is shown as a radio button option label in the Usefulness rating section of the suggestion feedback modal, representing a neutral usefulness score (2) indicating the suggested content belongs but does not add significant value.',
	},
	usefulness1: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.1',
		defaultMessage: 'Not Very Useful – Low-value or "fluff."',
		description:
			'The text is shown as a radio button option label in the Usefulness rating section of the suggestion feedback modal, representing a low usefulness score (1) indicating the suggested content has little value or limited relevance.',
	},
	usefulness0: {
		id: 'space-curation.suggestion-feedback-modal.usefulness.0',
		defaultMessage: 'Counter-productive – Distracting or adds noise.',
		description:
			'The text is shown as a radio button option label in the Usefulness rating section of the suggestion feedback modal, representing the lowest usefulness score (0) indicating the suggested content is counter-productive, distracting, or adds noise.',
	},

	redundancyTitle: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.title',
		defaultMessage: 'Redundancy (Uniqueness)',
		description: 'Title for the Redundancy feedback question',
	},
	redundancyQuestion: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.question',
		defaultMessage: 'Does the doc add info not already in the space?',
		description: 'Question text for the Redundancy feedback question',
	},
	redundancy3: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.3',
		defaultMessage: 'Entirely New – Information not found elsewhere in the space.',
		description:
			'The text is shown as a radio button option label in the Redundancy rating section of the suggestion feedback modal, representing the highest uniqueness score (3) indicating the suggested content is entirely new information not found elsewhere in the space.',
	},
	redundancy2: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.2',
		defaultMessage: 'Mostly New – Some overlap, but provides new insights.',
		description:
			'The text is shown as a radio button option label in the Redundancy rating section of the suggestion feedback modal, representing a mostly-unique score (2) indicating the suggested content has some overlap with existing content but still provides new insights.',
	},
	redundancy1: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.1',
		defaultMessage: 'High Overlap – Repeats most of what is already there.',
		description:
			'The text is shown as a radio button option label in the Redundancy rating section of the suggestion feedback modal, representing a high-overlap score (1) indicating the suggested content largely repeats what is already present in the space.',
	},
	redundancy0: {
		id: 'space-curation.suggestion-feedback-modal.redundancy.0',
		defaultMessage: 'Redundant – Information is already present (duplicate/near-duplicate).',
		description:
			'The text is shown as a radio button option label in the Redundancy rating section of the suggestion feedback modal, representing the lowest uniqueness score (0) indicating the suggested content is fully redundant or a duplicate of existing content in the space.',
	},

	explainabilityTitle: {
		id: 'space-curation.suggestion-feedback-modal.explainability.title',
		defaultMessage: 'Explainability',
		description: 'Title for the Explainability feedback question',
	},
	explainabilityQuestion: {
		id: 'space-curation.suggestion-feedback-modal.explainability.question',
		defaultMessage: 'Is it clear why this document was recommended for this space?',
		description: 'Question text for the Explainability feedback question',
	},
	explainability2: {
		id: 'space-curation.suggestion-feedback-modal.explainability.2',
		defaultMessage: 'Very Clear – I immediately see the connection.',
		description:
			'The text is shown as a radio button option label in the Explainability rating section of the suggestion feedback modal, representing the highest clarity score (2) indicating the user immediately understands why the content was suggested.',
	},
	explainability1: {
		id: 'space-curation.suggestion-feedback-modal.explainability.1',
		defaultMessage: "Vaguely Clear – I can see a possible link, but it's weak.",
		description:
			'The text is shown as a radio button option label in the Explainability rating section of the suggestion feedback modal, representing a partial clarity score (1) indicating the user can see a possible connection to the suggestion but it is not immediately obvious.',
	},
	explainability0: {
		id: 'space-curation.suggestion-feedback-modal.explainability.0',
		defaultMessage: 'Not Clear – The suggestion feels random or "black box."',
		description:
			'The text is shown as a radio button option label in the Explainability rating section of the suggestion feedback modal, representing the lowest clarity score (0) indicating the user cannot understand why the content was suggested and it feels random or irrelevant.',
	},

	recencyTitle: {
		id: 'space-curation.suggestion-feedback-modal.recency.title',
		defaultMessage: 'Recency & Freshness',
		description: 'Title for the Recency & Freshness feedback question',
	},
	recencyQuestion: {
		id: 'space-curation.suggestion-feedback-modal.recency.question',
		defaultMessage: 'How would you characterize the timeliness of this document?',
		description: 'Question text for the Recency & Freshness feedback question',
	},
	recency2: {
		id: 'space-curation.suggestion-feedback-modal.recency.2',
		defaultMessage:
			'Current/Evergreen – Information is up-to-date or foundational (always useful).',
		description:
			'The text is shown as a radio button option label in the Recency rating section of the suggestion feedback modal, representing the highest recency score (2) indicating the suggested content is current, up-to-date, or evergreen (foundational and always useful).',
	},
	recency1: {
		id: 'space-curation.suggestion-feedback-modal.recency.1',
		defaultMessage:
			'Historical Reference – Older but still holds some context or "lesson learned" value.',
		description:
			'The text is shown as a radio button option label in the Recency rating section of the suggestion feedback modal, representing a mid-level recency score (1) indicating the suggested content is older but still provides some historical context or background.',
	},
	recency0: {
		id: 'space-curation.suggestion-feedback-modal.recency.0',
		defaultMessage: 'Obsolete – The information is expired, superseded, or no longer true.',
		description:
			'The text is shown as a radio button option label in the Recency rating section of the suggestion feedback modal, representing the lowest recency score (0) indicating the suggested content is obsolete, superseded, or no longer accurate.',
	},

	permissionsPrivacyTitle: {
		id: 'space-curation.suggestion-feedback-modal.permissions-privacy.title',
		defaultMessage: 'Permissions & Privacy',
		description: 'Title for the Permissions & Privacy feedback question',
	},
	permissionsPrivacyQuestion: {
		id: 'space-curation.suggestion-feedback-modal.permissions-privacy.question',
		defaultMessage: 'Does it feel appropriate for this document to be surfaced here?',
		description: 'Question text for the Permissions & Privacy feedback question',
	},
	permissionsPrivacy2: {
		id: 'space-curation.suggestion-feedback-modal.permissions-privacy.2',
		defaultMessage: "Appropriate – Safe for general viewing within the space's context.",
		description: 'Option label for Permissions & Privacy score 2',
	},
	permissionsPrivacy1: {
		id: 'space-curation.suggestion-feedback-modal.permissions-privacy.1',
		defaultMessage: 'Questionable – Might contain semi-private info that feels "odd" to see.',
		description: 'Option label for Permissions & Privacy score 1',
	},
	permissionsPrivacy0: {
		id: 'space-curation.suggestion-feedback-modal.permissions-privacy.0',
		defaultMessage:
			'Inappropriate – Contains sensitive or private data that should not be surfaced.',
		description: 'Option label for Permissions & Privacy score 0',
	},

	additionalCommentsLabel: {
		id: 'space-curation.suggestion-feedback-modal.additional-comments.label',
		defaultMessage: 'Additional comments (optional)',
		description:
			'Label for the optional freeform feedback text area in the suggestion quality feedback modal',
	},
	additionalCommentsPlaceholder: {
		id: 'space-curation.suggestion-feedback-modal.additional-comments.placeholder',
		defaultMessage: 'Share any other thoughts about this suggestion…',
		description: 'Placeholder text for the optional freeform feedback text area',
	},
	searchSource: {
		id: 'space-curation.suggestion-feedback-modal.additional-comments.searchSource',
		defaultMessage: 'This suggestion was provided by the search pipeline',
		description: 'Text when suggestion is provided by search',
	},
	mlSource: {
		id: 'space-curation.suggestion-feedback-modal.additional-comments.mlSource',
		defaultMessage: 'This suggestion was provided by the ML pipeline',
		description: 'Text when suggestion is provided by ML',
	},
});

const FEEDBACK_QUESTIONS: FeedbackQuestion[] = [
	{
		key: 'topicalRelevance',
		part: 1,
		title: i18n.topicalRelevanceTitle,
		question: i18n.topicalRelevanceQuestion,
		options: [
			{ value: 3, label: i18n.topicalRelevance3 },
			{ value: 2, label: i18n.topicalRelevance2 },
			{ value: 1, label: i18n.topicalRelevance1 },
			{ value: 0, label: i18n.topicalRelevance0 },
		],
	},
	{
		key: 'placement',
		part: 1,
		title: i18n.placementTitle,
		question: i18n.placementQuestion,
		options: [
			{ value: 2, label: i18n.placement2 },
			{ value: 1, label: i18n.placement1 },
			{ value: 0, label: i18n.placement0 },
		],
	},
	{
		key: 'usefulness',
		part: 1,
		title: i18n.usefulnessTitle,
		question: i18n.usefulnessQuestion,
		options: [
			{ value: 4, label: i18n.usefulness4 },
			{ value: 3, label: i18n.usefulness3 },
			{ value: 2, label: i18n.usefulness2 },
			{ value: 1, label: i18n.usefulness1 },
			{ value: 0, label: i18n.usefulness0 },
		],
	},
	{
		key: 'redundancy',
		part: 1,
		title: i18n.redundancyTitle,
		question: i18n.redundancyQuestion,
		options: [
			{ value: 3, label: i18n.redundancy3 },
			{ value: 2, label: i18n.redundancy2 },
			{ value: 1, label: i18n.redundancy1 },
			{ value: 0, label: i18n.redundancy0 },
		],
	},
	{
		key: 'explainability',
		part: 2,
		title: i18n.explainabilityTitle,
		question: i18n.explainabilityQuestion,
		options: [
			{ value: 2, label: i18n.explainability2 },
			{ value: 1, label: i18n.explainability1 },
			{ value: 0, label: i18n.explainability0 },
		],
	},
	{
		key: 'recency',
		part: 2,
		title: i18n.recencyTitle,
		question: i18n.recencyQuestion,
		options: [
			{ value: 2, label: i18n.recency2 },
			{ value: 1, label: i18n.recency1 },
			{ value: 0, label: i18n.recency0 },
		],
	},
	{
		key: 'permissionsPrivacy',
		part: 2,
		title: i18n.permissionsPrivacyTitle,
		question: i18n.permissionsPrivacyQuestion,
		options: [
			{ value: 2, label: i18n.permissionsPrivacy2 },
			{ value: 1, label: i18n.permissionsPrivacy1 },
			{ value: 0, label: i18n.permissionsPrivacy0 },
		],
	},
];

const styles = cssMap({
	partHeader: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		marginBlockEnd: token('space.100'),
	},
	questionBlock: {
		paddingBlockEnd: token('space.200'),
	},
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.small'),
		cursor: 'pointer',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		transition: 'background-color 150ms ease, border-color 150ms ease',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: token('color.background.neutral'),
		},
	},
	optionRowSelected: {
		backgroundColor: token('color.background.selected'),
		borderColor: token('color.border.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	optionRowDisabled: {
		opacity: token('opacity.disabled'),
		cursor: 'not-allowed',
		'&:hover': {
			// @ts-expect-error - AllowedStyles expects different token values
			backgroundColor: 'transparent',
		},
	},
	radioCircle: {
		width: '16px',
		height: '16px',
		borderRadius: token('radius.full'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.bold'),
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioCircleSelected: {
		borderColor: token('color.border.selected'),
		backgroundColor: token('color.background.selected.bold'),
	},
	radioInnerDot: {
		width: '6px',
		height: '6px',
		borderRadius: token('radius.full'),
		// @ts-expect-error - color.icon.inverse is a valid color token for this inverted dot element
		backgroundColor: token('color.icon.inverse'),
	},
	skipRow: {
		paddingBlockStart: token('space.050'),
	},
	additionalCommentsSection: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
	},
	modalBody: {
		paddingBlock: token('space.200'),
	},
});

type OptionRowProps = {
	label: string;
	value: number;
	questionKey: string;
	isSelected: boolean;
	isDisabled: boolean;
	onSelect: (questionKey: string, value: number) => void;
};

const OptionRow = ({
	label,
	value,
	questionKey,
	isSelected,
	isDisabled,
	onSelect,
}: OptionRowProps) => {
	const handleClick = useCallback(() => {
		if (!isDisabled) {
			onSelect(questionKey, value);
		}
	}, [isDisabled, onSelect, questionKey, value]);

	return (
		<Pressable
			role="radio"
			aria-checked={isSelected}
			aria-disabled={isDisabled}
			xcss={cx(
				styles.optionRow,
				isSelected && styles.optionRowSelected,
				isDisabled && styles.optionRowDisabled,
			)}
			onClick={handleClick}
		>
			<Box xcss={cx(styles.radioCircle, isSelected && styles.radioCircleSelected)}>
				{isSelected && <Box xcss={styles.radioInnerDot} />}
			</Box>
			<Text size="small">{label}</Text>
		</Pressable>
	);
};

type SuggestionFeedbackModalProps = {
	suggestion: Suggestion;
	spaceId?: string;
	spaceKey?: string;
	onClose: () => void;
	onSubmit?: () => void;
};

export const SuggestionFeedbackModal = ({
	suggestion,
	spaceId,
	spaceKey,
	onClose,
	onSubmit,
}: SuggestionFeedbackModalProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	const { userId } = useSessionData();

	const [answers, setAnswers] = useState<Record<string, number | null>>(() =>
		Object.fromEntries(FEEDBACK_QUESTIONS.map((q) => [q.key, null])),
	);
	const [skipped, setSkipped] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(FEEDBACK_QUESTIONS.map((q) => [q.key, false])),
	);
	const [additionalComments, setAdditionalComments] = useState<string>('');

	const handleSelectOption = useCallback((questionKey: string, value: number) => {
		setAnswers((prev) => ({ ...prev, [questionKey]: value }));
	}, []);

	const handleToggleSkip = useCallback((questionKey: string, checked: boolean) => {
		setSkipped((prev) => ({ ...prev, [questionKey]: checked }));
		if (checked) {
			setAnswers((prev) => ({ ...prev, [questionKey]: null }));
		}
	}, []);

	const handleSubmit = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'submitted',
				actionSubject: 'suggestionFeedback',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					userId,
					suggestionAri: suggestion.id,
					suggestionUrl: suggestion.url,
					suggestedParentId: suggestion.parentId,
					sourceType: getSuggestionProductKeyFromContentUrl(suggestion.url),
					providerId: suggestion.providerId,
					topicalRelevance: answers.topicalRelevance,
					placement: answers.placement,
					usefulness: answers.usefulness,
					redundancy: answers.redundancy,
					explainability: answers.explainability,
					recency: answers.recency,
					permissionsPrivacy: answers.permissionsPrivacy,
					additionalComments: additionalComments.trim() || undefined,
				},
			},
		}).fire();
		onSubmit?.();
		onClose();
	}, [
		createAnalyticsEvent,
		spaceId,
		spaceKey,
		suggestion,
		answers,
		additionalComments,
		onClose,
		onSubmit,
		userId,
	]);

	const questionsWithPartHeaders = useMemo(
		() =>
			FEEDBACK_QUESTIONS.map((question, index) => ({
				...question,
				showPartHeader: index === 0 || question.part !== FEEDBACK_QUESTIONS[index - 1].part,
			})),
		[],
	);

	const handleSkipChange = useCallback(
		(questionKey: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
			handleToggleSkip(questionKey, e.target.checked),
		[handleToggleSkip],
	);

	return (
		<Modal onClose={onClose} width="medium">
			<ModalHeader>
				<Stack>
					<ModalTitle>{formatMessage(i18n.modalTitle)}</ModalTitle>
					<Text>
						{suggestion.providerId?.includes('search')
							? formatMessage(i18n.searchSource)
							: suggestion.providerId?.includes('ml-feature-store') && formatMessage(i18n.mlSource)}
					</Text>
				</Stack>
			</ModalHeader>
			<ModalBody>
				<Box xcss={styles.modalBody}>
					<Stack space="space.0">
						{questionsWithPartHeaders.map((question) => {
							const isSkipped = skipped[question.key];
							const selectedValue = answers[question.key];

							return (
								<React.Fragment key={question.key}>
									{question.showPartHeader && (
										<Box xcss={styles.partHeader}>
											<Heading size="xsmall">
												{question.part === 1
													? formatMessage(i18n.part1Header)
													: formatMessage(i18n.part2Header)}
											</Heading>
										</Box>
									)}
									<Box xcss={styles.questionBlock}>
										<Stack space="space.075">
											<Text weight="semibold">{formatMessage(question.title)}</Text>
											<Text size="small" color="color.text.subtle">
												{formatMessage(question.question)}
											</Text>
											<Box role="radiogroup" aria-label={formatMessage(question.title)}>
												{
													// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
													question.options.map((option) => (
														<OptionRow
															key={option.value}
															label={formatMessage(option.label)}
															value={option.value}
															questionKey={question.key}
															isSelected={!isSkipped && selectedValue === option.value}
															isDisabled={isSkipped}
															onSelect={handleSelectOption}
														/>
													))
												}
											</Box>
											<Flex xcss={styles.skipRow}>
												<Checkbox
													label={formatMessage(i18n.skipLabel)}
													isChecked={isSkipped}
													onChange={handleSkipChange(question.key)}
												/>
											</Flex>
										</Stack>
									</Box>
								</React.Fragment>
							);
						})}
						<Box xcss={styles.additionalCommentsSection}>
							<Stack space="space.075">
								<Text weight="semibold">{formatMessage(i18n.additionalCommentsLabel)}</Text>
								<TextArea
									value={additionalComments}
									onChange={(e) => setAdditionalComments(e.target.value)}
									placeholder={formatMessage(i18n.additionalCommentsPlaceholder)}
									maxLength={500}
									minimumRows={3}
								/>
							</Stack>
						</Box>
					</Stack>
				</Box>
			</ModalBody>
			<ModalFooter>
				<Button appearance="subtle" onClick={onClose}>
					{formatMessage(i18n.cancelButton)}
				</Button>
				<Button appearance="primary" onClick={handleSubmit}>
					{formatMessage(i18n.submitButton)}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
